import { useState, useCallback } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useBoundValue } from "../hooks/use-bound-value"
import { cn } from "../lib/utils"

const FALLBACK_PALETTE = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
  "var(--primary)",
  "var(--destructive)",
  "var(--muted-foreground)",
]

export function ColorPickerRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()

  const [selected, setSelected] = useBoundValue<string>(
    element.bind,
    resolveSymbol,
    element.defaultColor ?? element.value ?? "var(--primary)",
    String,
  )

  const palette = element.palette ?? FALLBACK_PALETTE

  const handleSelect = useCallback(
    (color: string) => {
      setSelected(color)
      dispatchEvent(element.id, "change", { value: color })
    },
    [element.id, dispatchEvent, setSelected],
  )

  return (
    <div
      className={cn("flex flex-col gap-3", element.className)}
      data-testid={`color-picker-${element.id}`}
    >
      <div
        className="w-12 h-12 rounded-lg border border-border shadow-sm"
        style={{ backgroundColor: selected }}
        aria-label={`Selected color: ${selected}`}
      />
      <div className="flex flex-row flex-wrap gap-1.5">
        {palette.map((color: string) => (
          <button
            key={color}
            type="button"
            className={cn(
              "w-8 h-8 rounded-md border cursor-pointer transition-transform hover:scale-110",
              selected === color ? "border-primary border-2" : "border-border",
            )}
            style={{ backgroundColor: color }}
            onClick={() => handleSelect(color)}
            aria-label={`Select color ${color}`}
          >
            {selected === color && (
              <svg
                className="w-4 h-4 mx-auto"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="3"
                style={{
                  filter:
                    "drop-shadow(0 0 1px rgba(0,0,0,0.7)) drop-shadow(0 0 1px rgba(0,0,0,0.7))",
                }}
              >
                <path d="M5 13l4 4L19 7" />
              </svg>
            )}
          </button>
        ))}
      </div>
    </div>
  )
}
